import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'acm-text-size'
const CHANGE_EVENT = 'acm-text-size-change'

type TextSize = 'standard' | 'large'

const readSaved = (): TextSize => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'large' ? 'large' : 'standard'
    }
    catch {
        return 'standard'
    }
}

interface TextSizeToggleProps {
    tone?: 'dark' | 'light'
    className?: string
}

const TextSizeToggle: FC<TextSizeToggleProps> = ({ tone = 'dark', className }): ReactNode => {
    // Server-safe default; the saved choice is read after mount.
    const [size, setSize] = useState<TextSize>('standard')

    useEffect(() => {
        const sync = () => {
            const saved = readSaved()

            setSize(saved)
            document.documentElement.dataset.textSize = saved
        }

        sync()
        window.addEventListener(CHANGE_EVENT, sync)

        return () => window.removeEventListener(CHANGE_EVENT, sync)
    }, [])

    const choose = (next: TextSize) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, next)
        }
        catch {
            // Storage can be unavailable in private browsing; the choice still applies to this visit.
        }

        setSize(next)
        document.documentElement.dataset.textSize = next
        window.dispatchEvent(new Event(CHANGE_EVENT))
    }

    const base = 'min-h-10 rounded-md px-3 font-bold transition-colors'
    const on = tone === 'dark' ? 'bg-white text-navy' : 'bg-navy text-white'
    const off = tone === 'dark' ? 'text-white hover:bg-white/15' : 'text-navy hover:bg-secondary'

    return (
        <div role='group' aria-label='Text size' className={cn('flex items-center gap-1', className)}>
            <span className={cn('mr-1 text-sm', tone === 'dark' ? 'text-white/85' : 'text-muted-foreground')}>
                Text size
            </span>

            <button
                type='button'
                aria-pressed={size === 'standard'}
                onClick={() => choose('standard')}
                className={cn(base, 'text-sm', size === 'standard' ? on : off)}
            >
                Standard
            </button>

            <button
                type='button'
                aria-pressed={size === 'large'}
                onClick={() => choose('large')}
                className={cn(base, 'text-base', size === 'large' ? on : off)}
            >
                Larger
            </button>
        </div>
    )
}

export default TextSizeToggle
